// "Design a wall": drag a box over a wall, describe a look, pick one of three
// AI textures and it goes on that wall. See src/services/wallDesign.ts.
import React, { useEffect, useRef, useState } from 'react';
import { useThree } from '@react-three/fiber';
import { Check, Loader, RotateCcw, Sparkles, Wand2, X } from 'lucide-react';
import {
  applyWallDesign, canvasEl, captureWall, pickWallTarget, requestWallDesigns, setWallDesignThree,
  type DesignOption, type ScreenRect, type WallTarget,
} from '../services/wallDesign';
import { useWorldStore } from '../stores/worldStore';
import './WallDesigner.css';

/** Mount inside the editor <Canvas>: shares the renderer/scene/camera with the overlay. */
export const WallDesignBridge: React.FC = () => {
  const { gl, scene, camera } = useThree();
  useEffect(() => { setWallDesignThree({ gl, scene, camera }); return () => setWallDesignThree(null); }, [gl, scene, camera]);
  return null;
};

const IDEAS = ['Lush botanical mural', 'Art deco gold and emerald', 'Moody dark panelling', 'Soft limewash plaster', 'Bold geometric wallpaper'];

type Phase = 'select' | 'describe' | 'working' | 'pick';

export const WallDesigner: React.FC<{ worldId?: string; onClose: () => void }> = ({ worldId, onClose }) => {
  const [phase, setPhase] = useState<Phase>('select');
  const [drag, setDrag] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);
  const [target, setTarget] = useState<WallTarget | null>(null);
  const [shot, setShot] = useState<{ crop: string; view: string } | null>(null);
  const [prompt, setPrompt] = useState('');
  const [err, setErr] = useState('');
  const [options, setOptions] = useState<DesignOption[]>([]);
  const [analysis, setAnalysis] = useState('');
  const [applied, setApplied] = useState<string | null>(null);
  const layer = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && phase !== 'working') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, onClose]);

  // Coordinates relative to the canvas, whatever sits around it.
  const local = (e: React.PointerEvent) => {
    const c = canvasEl()?.getBoundingClientRect();
    return { x: e.clientX - (c?.left ?? 0), y: e.clientY - (c?.top ?? 0) };
  };
  const box = (d: NonNullable<typeof drag>): ScreenRect => ({
    x: Math.min(d.x0, d.x1), y: Math.min(d.y0, d.y1), w: Math.abs(d.x1 - d.x0), h: Math.abs(d.y1 - d.y0),
  });

  const finishDrag = () => {
    if (!drag) return;
    const r = box(drag);
    setDrag(null);
    if (r.w < 24 || r.h < 24) { setErr('Drag a bigger box over the wall.'); return; }
    const t = pickWallTarget(r);
    if (!t) { setErr('That area isn’t a wall you can retexture. Try a box or plane wall, or part of a model.'); return; }
    setErr('');
    setTarget(t);
    setShot(captureWall(r));
    setPhase('describe');
  };

  const generate = async () => {
    if (!shot || !target) return;
    setPhase('working'); setErr(''); setApplied(null);
    try {
      const r = await requestWallDesigns(shot.crop, shot.view, prompt.trim(), target.aspect);
      setOptions(r.options); setAnalysis(r.analysis);
      setPhase('pick');
    } catch (e: any) {
      setErr(e?.message || 'Design failed.');
      setPhase('describe');
    }
  };

  const apply = (o: DesignOption) => {
    if (!target) return;
    applyWallDesign(worldId, target, o);
    setApplied(o.url);
  };

  const restart = () => { setPhase('select'); setTarget(null); setShot(null); setOptions([]); setApplied(null); setErr(''); };

  // Keep the selection out of the way while designing.
  useEffect(() => { useWorldStore.getState().setSelectedObjectId(null); }, []);

  const r = drag ? box(drag) : null;
  const c = canvasEl()?.getBoundingClientRect();

  return (
    <>
      {phase === 'select' && (
        <div
          ref={layer}
          className="wd-layer"
          style={c ? { left: c.left, top: c.top, width: c.width, height: c.height } : undefined}
          onPointerDown={(e) => { (e.target as HTMLElement).setPointerCapture(e.pointerId); const p = local(e); setDrag({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); }}
          onPointerMove={(e) => { if (drag) { const p = local(e); setDrag({ ...drag, x1: p.x, y1: p.y }); } }}
          onPointerUp={finishDrag}
        >
          {r && <div className="wd-box" style={{ left: r.x, top: r.y, width: r.w, height: r.h }} />}
          <div className="wd-hint">
            <Wand2 size={15} /> Drag a box over the wall you want to redesign
            <button onClick={(e) => { e.stopPropagation(); onClose(); }} onPointerDown={(e) => e.stopPropagation()}>Cancel</button>
          </div>
          {err && <div className="wd-hint wd-hint-err">{err}</div>}
        </div>
      )}

      {phase !== 'select' && (
        <div className="wd-panel" role="dialog" aria-label="Design a wall">
          <header>
            <span><Sparkles size={15} /> Design a wall</span>
            <button className="wd-x" onClick={onClose} disabled={phase === 'working'} aria-label="Close"><X size={16} /></button>
          </header>

          {shot && target && (
            <div className="wd-target">
              <img src={shot.crop} alt="Selected wall" />
              <div>
                <strong>{target.label}</strong>
                <span>{target.kind === 'model' ? 'Part of a model: the texture follows its own mapping.' : `Wall about ${target.aspect >= 1 ? `${target.aspect.toFixed(1)}:1` : `1:${(1 / target.aspect).toFixed(1)}`}`}</span>
                <button className="wd-link" onClick={restart} disabled={phase === 'working'}><RotateCcw size={12} /> Select again</button>
              </div>
            </div>
          )}

          {(phase === 'describe' || phase === 'working') && (
            <>
              <label className="wd-label" htmlFor="wd-prompt">Any direction? <small>optional</small></label>
              <textarea
                id="wd-prompt" rows={3} value={prompt} maxLength={600} autoFocus disabled={phase === 'working'}
                placeholder="Leave blank and the AI designs from the room, or steer it, e.g. a symmetrical mystical accent wall with lush greenery"
                onChange={(e) => setPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void generate(); }}
              />
              <div className="wd-ideas">
                {IDEAS.map((i) => <button key={i} disabled={phase === 'working'} onClick={() => setPrompt(i)}>{i}</button>)}
              </div>
              {err && <p className="wd-err" role="alert">{err}</p>}
              <button className="wd-go" onClick={generate} disabled={phase === 'working'}>
                {phase === 'working' ? <><Loader size={15} className="wd-spin" /> Reading the room and designing…</> : <><Sparkles size={15} /> {prompt.trim() ? 'Design 3 options' : 'Design 3 options for this room'}</>}
              </button>
            </>
          )}

          {phase === 'pick' && (
            <>
              {analysis && <p className="wd-analysis">{analysis}</p>}
              <div className="wd-options">
                {options.map((o) => (
                  <button key={o.url} className={`wd-opt ${applied === o.url ? 'is-on' : ''}`} onClick={() => apply(o)}>
                    <img src={o.url} alt={o.title} crossOrigin="anonymous" />
                    <span>{applied === o.url && <Check size={13} />} {o.title}</span>
                    <em>{o.kind === 'pattern' ? 'Repeating pattern' : 'Mural'}</em>
                  </button>
                ))}
              </div>
              <p className="wd-note">{applied ? 'Applied. Try another, or Ctrl+Z to undo.' : 'Click one to put it on the wall.'}</p>
              <div className="wd-row">
                <button className="wd-secondary" onClick={() => setPhase('describe')}><RotateCcw size={14} /> New ideas</button>
                <button className="wd-go" onClick={onClose}><Check size={15} /> Done</button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
};
